import { useEffect, useState } from 'react'
import {
  Box,
  HStack,
  VStack,
  Heading,
  Button,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiBookOpen, FiSave } from 'react-icons/fi'
import {
  useCampaignScriptConfig,
  useSaveCampaignScriptConfig,
} from '../../hooks/useCampaign'
import { ScriptConfigForm, toSaveAlternates, type ScriptConfigValue } from './ScriptConfigForm'

interface Props {
  campaignId: string
}

export function CampaignScriptCard({ campaignId }: Props) {
  const { data: config, isLoading } = useCampaignScriptConfig(campaignId)
  const saveMutation = useSaveCampaignScriptConfig(campaignId)

  const [value, setValue] = useState<ScriptConfigValue>({ defaultScriptId: null, alternates: [] })
  const [initialized, setInitialized] = useState(false)

  // Initialize from server data
  useEffect(() => {
    if (config && !initialized) {
      setValue({
        defaultScriptId: config.defaultScriptId,
        alternates: config.alternateScripts.map((a) => ({
          scriptId: a.scriptId,
          conditionType: a.conditionType,
          conditionTagId: a.conditionTagId,
          conditionTurfMapId: a.conditionTurfMapId,
          priority: a.priority,
        })),
      })
      setInitialized(true)
    }
  }, [config, initialized])

  const handleSave = async () => {
    try {
      await saveMutation.mutateAsync({
        defaultScriptId: value.defaultScriptId,
        alternateScripts: toSaveAlternates(value.alternates),
      })
      toaster.create({
        title: 'Script configuration saved',
        type: 'success',
        duration: 3000,
      })
    } catch {
      toaster.create({
        title: 'Failed to save script configuration',
        type: 'error',
        duration: 5000,
      })
    }
  }

  if (isLoading) {
    return (
      <Box bg="surface" p={6} borderRadius="lg" shadow="md">
        <VStack py={6}>
          <Spinner size="lg" color="accent" />
        </VStack>
      </Box>
    )
  }

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack justify="space-between" mb={4}>
        <HStack gap={2}>
          <Icon color="accent" asChild><FiBookOpen /></Icon>
          <Heading size="sm">Campaign Script</Heading>
        </HStack>
        <Button
          size="sm"
          colorPalette="brand"
          onClick={handleSave}
          loading={saveMutation.isPending}><FiSave />Save
                  </Button>
      </HStack>

      <ScriptConfigForm campaignId={campaignId} value={value} onChange={setValue} />
    </Box>
  );
}
